import { createFileRoute, Link, useSearch, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth-context";
import { useSubscription } from "@/hooks/useSubscription";

export const Route = createFileRoute("/checkout/return")({
  validateSearch: (search: Record<string, unknown>) => ({
    session_id: (search.session_id as string) || undefined,
  }),
  head: () => ({
    meta: [{ title: "Payment Complete | XAU Pro Academy" }, { name: "robots", content: "noindex, nofollow" }],
  }),
  component: CheckoutReturnPage,
});

function CheckoutReturnPage() {
  const { session_id } = useSearch({ from: "/checkout/return" });
  const { user, refreshProfile } = useAuth();
  const { isActive, refetch } = useSubscription();
  const navigate = useNavigate();
  const [elapsed, setElapsed] = useState(0);

  // Poll for activation: webhook usually completes within 1–5s
  useEffect(() => {
    if (!session_id || !user) return;
    if (isActive) {
      refreshProfile();
      const t = setTimeout(() => navigate({ to: "/dashboard" }), 1200);
      return () => clearTimeout(t);
    }
    if (elapsed >= 15) return;
    const t = setTimeout(() => {
      setElapsed((e) => e + 1);
      refetch();
    }, 1000);
    return () => clearTimeout(t);
  }, [session_id, user, isActive, elapsed, navigate, refetch, refreshProfile]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-deep px-4">
      <div className="max-w-md rounded-2xl border border-gold/40 bg-card p-8 text-center shadow-gold">
        {!session_id ? (
          <>
            <Loader2 className="mx-auto h-10 w-10 animate-spin text-gold" />
            <p className="mt-4 text-sm text-muted-foreground">No session information found.</p>
            <Button asChild variant="outline" className="mt-6">
              <Link to="/pricing">Back to Pricing</Link>
            </Button>
          </>
        ) : isActive ? (
          <>
            <CheckCircle2 className="mx-auto h-14 w-14 text-emerald" />
            <h1 className="mt-4 font-display text-2xl font-bold">You're In</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Access activated, taking you to your dashboard…
            </p>
            <Button asChild variant="gold" size="lg" className="mt-6 w-full">
              <Link to="/dashboard">Go to Dashboard now</Link>
            </Button>
          </>
        ) : elapsed >= 15 ? (
          <>
            <CheckCircle2 className="mx-auto h-14 w-14 text-gold" />
            <h1 className="mt-4 font-display text-2xl font-bold">Payment Received</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Your payment came through but activation is taking longer than usual. Refresh your dashboard in a minute, or contact support if it doesn't appear.
            </p>
            <Button asChild variant="gold" size="lg" className="mt-6 w-full">
              <Link to="/dashboard">Open Dashboard</Link>
            </Button>
          </>
        ) : (
          <>
            <Loader2 className="mx-auto h-14 w-14 animate-spin text-gold" />
            <h1 className="mt-4 font-display text-2xl font-bold">Activating your access…</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Payment received. Unlocking premium features ({elapsed}s)…
            </p>
          </>
        )}
      </div>
    </div>
  );
}
